// libs
import React, { memo, useEffect } from 'react';

// helpers
import AnalyticsSDK from '@nykaa/analytics-sdk';
import { getDeviceType, getEnvironment, getCachedCustomerId } from './helpers';
import { DEFAULT_STORE } from 'shared/helpers/storeUrl';

// components
import Pageload from 'shared/components/Analytics/Pageload';

// constants
import { VERTICAL_NAME } from 'shared/constants';

interface InitializeProps {
  customerId: string;
  onIntlNetwork: boolean;
  storeId: string;
  excludedEventNames: string[];
  eventsThreshold: number;
  batchInterval: number;
  maxSessionRefreshTime: number;
  sessionExpiryTime: number;
}

declare global {
  interface Window {
    na: any;
  }
}

/**
 * React component to initialize analytics SDK
 */
function Initialize({
  customerId,
  onIntlNetwork,
  storeId,
  excludedEventNames,
  eventsThreshold,
  batchInterval,
  maxSessionRefreshTime,
  sessionExpiryTime,
}: InitializeProps) {
  useEffect(() => {
    if (onIntlNetwork) {
      return;
    }

    const localCustomerId = customerId.length > 0 ? customerId : getCachedCustomerId();

    const sdk = new AnalyticsSDK({
      analyticsEndpoint: process.env.ANALYTICS_ENDPOINT,
      environment: getEnvironment(),
      vertical: VERTICAL_NAME,
      platform: getDeviceType(),
      appVersion: __VERSION__,
      customerId: localCustomerId,
      batchInterval,
      excludedEventNames,
      eventsThreshold,
      retinaSdkPlatform: getDeviceType() === 'dweb' ? 'dweb' : 'mweb',
      maxSessionRefreshTime: maxSessionRefreshTime,
      sessionExpiryTime: sessionExpiryTime,
    });

    const storeSelected = storeId === DEFAULT_STORE ? 'fashion' : storeId;

    if (storeSelected) {
      const enrichEvent = (data: Record<string, any>) => ({
        store_selected: storeSelected,
        ...data,
        ...(data.tracking_metadata && {
          tracking_metadata: { store_selected: storeSelected, ...data.tracking_metadata },
        }),
      });

      const originalSendEvent = sdk.sendEvent.bind(sdk);
      sdk.sendEvent = (eventName: string, data: Record<string, any>) => {
        originalSendEvent(eventName, enrichEvent(data));
      };

      const originalSendMultipleEvents = sdk.sendMultipleEvents.bind(sdk);
      sdk.sendMultipleEvents = (multipleEvents: Record<string, any>[]) => {
        const enrichedEvents = multipleEvents.map((event) => enrichEvent(event));
        originalSendMultipleEvents(enrichedEvents);
      };

      const originalSendImpressionEvent = sdk.sendImpressionEvent.bind(sdk);
      sdk.sendImpressionEvent = (eventName: string, impressionData: any) => {
        originalSendImpressionEvent(eventName, enrichEvent(impressionData));
      };
    }

    window.na = sdk;

    return () => {
      sdk.destroy();
    };
  }, [
    customerId,
    onIntlNetwork,
    storeId,
    batchInterval,
    excludedEventNames,
    eventsThreshold,
    maxSessionRefreshTime,
    sessionExpiryTime,
  ]);

  return <Pageload />;
}

export default memo(Initialize);
